import { useEffect, useRef } from "react";
import { Button, Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader } from "@/components/ui";
import type { UseVoiceResult } from "@/hooks/useVoice";

export interface CallDialogProps {
  open: boolean;
  contactName: string;
  phone: string;
  voice: UseVoiceResult;
  /** Called once, when the dialog should close — either the user dismissed
   *  it manually, or a live call just ended (disconnected/canceled). The
   *  caller is expected to refetch the contact overview here so the just-
   *  logged call shows up in History. */
  onClose: () => void;
}

function formatElapsed(totalSeconds: number): string {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}

function statusLabel(voice: UseVoiceResult): string {
  switch (voice.status) {
    case "connecting":
      return "Connecting…";
    case "oncall":
      return `On call ${formatElapsed(voice.elapsedSeconds)}`;
    case "error":
      return "Call failed";
    case "idle":
    default:
      return "Call ended";
  }
}

/**
 * In-app call panel (Phase 15) — shown while `useVoice()`'s Device places
 * and runs a browser (WebRTC) call to a contact. The dialog itself holds
 * no call state; it only renders whatever `voice` (owned by the caller,
 * ContactDetailPage) reports, so a live call survives independently of
 * the dialog's own open/closed lifecycle.
 *
 * Auto-closes the moment a call that actually got underway (connecting or
 * on-call) returns to "idle" — that is Device/Call's own disconnect/cancel
 * signal, per useVoice's finalize(). An "error" status (most commonly a
 * denied mic permission) is left on screen with a Close button instead of
 * auto-dismissing, so the user actually sees why the call didn't happen.
 */
export function CallDialog({ open, contactName, phone, voice, onClose }: CallDialogProps) {
  const prevStatusRef = useRef(voice.status);

  useEffect(() => {
    const prev = prevStatusRef.current;
    prevStatusRef.current = voice.status;
    if (!open) return;
    if ((prev === "connecting" || prev === "oncall") && voice.status === "idle") {
      onClose();
    }
    // onClose is expected to be referentially stable-ish (or at least
    // idempotent) from the caller; re-running this effect on every
    // onClose identity change would risk missing the prev/next status
    // transition if the caller re-renders mid-call.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voice.status, open]);

  function handleDialogClose() {
    if (voice.status === "connecting" || voice.status === "oncall") {
      voice.hangup();
      // The disconnect event drives the effect above, which calls
      // onClose() once the Call actually tears down.
      return;
    }
    voice.reset();
    onClose();
  }

  const isLive = voice.status === "connecting" || voice.status === "oncall";

  return (
    <Dialog open={open} onClose={handleDialogClose} ariaLabel="Call" disableOverlayClose={isLive}>
      <DialogContent size="sm">
        <DialogHeader onClose={handleDialogClose}>Call</DialogHeader>
        <DialogBody className="space-y-4 text-center">
          <div>
            <p className="text-lg font-semibold text-text-primary">{contactName}</p>
            <p className="text-sm text-text-secondary">{phone}</p>
          </div>

          <p
            className="text-sm font-medium text-text-primary"
            data-testid="call-status"
            aria-live="polite"
          >
            {statusLabel(voice)}
          </p>

          {voice.status === "error" && voice.error && (
            <p className="text-sm text-danger" data-testid="call-error">
              {voice.error}
            </p>
          )}
        </DialogBody>
        <DialogFooter className="justify-center">
          {isLive ? (
            <>
              <Button
                variant={voice.muted ? "primary" : "secondary"}
                onClick={() => voice.mute(!voice.muted)}
              >
                {voice.muted ? "Unmute" : "Mute"}
              </Button>
              <Button variant="danger" onClick={() => voice.hangup()}>
                Hang up
              </Button>
            </>
          ) : (
            <Button variant="secondary" onClick={handleDialogClose}>
              Close
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
